import { EditOutlined } from "@mui/icons-material";
import { useMutation } from "@tanstack/react-query";
import { Button as AntButton, Space, Tooltip } from "antd";
import moment from "moment";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { dateTimeFormat, roles } from "~/config";
import useAuthorization from "~/hooks/useAuthorization";
import useColumnSearch from "~/hooks/useColumnSearch";
import MasterTable from "~/pages/components/MasterTable";
import { getCategoryData } from "~/services/category.service";
import { getTableParams } from "~/utils/utils";

const CategoryMaster = () => {
  const navigate = useNavigate();
  const { getColumnSearchProps, setSearchText, setSearchedColumn } =
    useColumnSearch();
  const { userHasRequiredRole } = useAuthorization([roles.ADMIN]);

  const [tableParams, setTableParams] = useState({
    pagination: {
      current: 1,
      pageSize: 25,
    },
  });

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);

    mutate(
      getTableParams({
        pagination: tableParams.pagination,
        filters: selectedKeys[0],
      })
    );
  };

  const columns = [
    {
      title: "Main Categroy Name",
      dataIndex: "Cat_Name",
      key: "Cat_Name",
      fixed: "left",
      ...getColumnSearchProps("Cat_Name", handleSearch, "Category Name"),
      width: 250,
    },
    {
      title: "Sub Categroy Name",
      dataIndex: "Cat_Value",
      key: "Cat_Value",
      width: 200,
      ellipsis: { showTitle: false },
      render: (value) => (
        <Tooltip placement="leftTop" title={value}>
          {value}
        </Tooltip>
      ),
    },
    {
      title: "Description",
      dataIndex: "Cat_Desc",
      key: "Cat_Desc",
      width: 200,
      ellipsis: { showTitle: false },
      render: (value) => (
        <Tooltip placement="leftTop" title={value}>
          {value}
        </Tooltip>
      ),
    },
    {
      title: "Created At",
      dataIndex: "Created_Dt",
      key: "Created_Dt",
      width: 200,
      render: (record) =>
        record === null ? "" : moment(record).format(dateTimeFormat),
    },
    {
      title: "Created By",
      dataIndex: "created_by.Login_Name",
      key: "created_by.Login_Name",
      width: 200,
    },
    {
      title: "Modified At",
      dataIndex: "Modified_Dt",
      key: "Modified_Dt",
      width: 200,
      render: (record) =>
        record === null ? "" : moment(record).format(dateTimeFormat),
    },
    {
      title: "Modified By",
      dataIndex: "modified_by.Login_Name",
      key: "modified_by.Login_Name",
      width: 200,
    },
    ...(userHasRequiredRole ? [{
      title: "Action",
      key: "action",
      fixed: "right",
      width: 100,
      render: (rowData, record) => (
        <Space size="middle">
          <Tooltip title="Edit">
            <AntButton
              type="primary"
              shape="circle"
              icon={<EditOutlined />}
              onClick={() =>
                navigate(
                  `/update/category-master/${rowData.Cat_Name.trim().replace(
                    /\s+/g,
                    "-"
                  )}`,
                  {
                    state: rowData,
                  }
                )
              }
            />
          </Tooltip>
        </Space>
      ),
    }]: []),
  ];

  const {
    data: categoryData,
    isLoading,
    mutate,
  } = useMutation({
    mutationKey: ["categoryData"],
    mutationFn: (payloads) => getCategoryData(payloads),
    onSuccess: (data) => {
      // Modify the data response here
      // Filter the data based on MemberName containing "Group"
      setTableParams({
        ...tableParams,
        pagination: {
          ...tableParams.pagination,
          total: data?.data?.count,
          // 200 is mock data, you should read it from server
          // total: data.totalCount,
        },
      });
      console.log("ON SUCCESS", data);
    },
  });

  const handleTableChange = (pagination, filters, sorter) => {
    if (typeof pagination === "object") {
      mutate(getTableParams({ pagination }));

      setTableParams({
        pagination,
        filters,
        ...sorter,
      });
    }
  };

  useEffect(() => {
    mutate(getTableParams(tableParams));
  }, []);

  return (
    <MasterTable
      cardHeaderTitle="Category Master"
      navigateTo="/create/category-master"
      cardButtonText="ADD CATEGORY"
      columns={columns}
      loading={isLoading}
      // expandable={{ expandedRowRender, defaultExpandedRowKeys: ['0'] }}
      scroll={{ y: 350, x: 1800 }}
      pagination={{
        showSizeChanger: true,
        showTotal: (total, range) =>
          `${range[0]}-${range[1]} of ${total} items`,
        ...tableParams.pagination,
      }}
      onChange={handleTableChange}
      dataSource={categoryData?.data?.rows || []}
    />
  );
};

export default CategoryMaster;
